import { WORKSHEET_TEMPLATE } from './worksheet-templates';

const VALID_TYPES = ['string', 'number', 'date', 'time'];
const CELL_PADDING = 2;

const formatStringCell = (value, cellRef) => {
  let cleanValue = value ?? '';
  const escapeExcelString = (str) => {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      '\'': '&#39;',
    };

    return str.replace(/[&<>"']/g, m => map[m]);
  };
  cleanValue = escapeExcelString(String(cleanValue).trim());

  return `<c r="${cellRef}" t="inlineStr"><is><t>${cleanValue}</t></is></c>`;
};

const createExcelDate = (date) => {
  const epochDiff = 25569.0; // difference between JS date epoch and excel epoch in days
  const dayInMs = 1000 * 60 * 60 * 24;

  return epochDiff + ((date.getTime() - (date.getTimezoneOffset() * 60 * 1000)) / (dayInMs));
};

/**
 * Class used to format data collection into Office Open XML format for .xlsx files
 * Link below provides a PDF (Part 1) of OOXML documentation
 * @see {@link https://www.ecma-international.org/publications-and-standards/standards/ecma-376/}
 */
export class XLSXFormatter {
  columns = [];

  /**
   * Generate worksheet xml string from data
   * @param {Record<string, any>} data user data
   * @param {Array<ExcelColumn>} columnConfig column configuration
   * @returns {string} xlsx formatted worksheet string
   */
  generateWorksheet(data, columnConfig) {
    this.prepareColumnConfig(columnConfig);
    const generateHeaderRow = (columns) => {
      const headerCells = columns.reduce((headerStr, col) => {
        const cellR = col.refLetter;
        const name = col.name;
        const cell = `<c r="${cellR}1" t="inlineStr" s="1"><is><t>${name}</t></is></c>`;
        return `${headerStr}${cell}`;
      }, '');
      return `<row r="1">${headerCells}</row>`;
    };
    const generateColWidths = columns => columns.reduce((cols, col, idx) => {
      const colIndex = idx + 1;
      const colWidth = col.width;
      const colStr = `<col min="${colIndex}" max="${colIndex}" width="${colWidth}" bestFit="1" customWidth="1" />`;
      return `${cols}${colStr}`;
    }, '');
    const dataHeader = generateHeaderRow(this.columns);
    const dataBody = this.generateRows(data);
    const sheetData = `${dataHeader}${dataBody}`;
    const colStyles = generateColWidths(this.columns);
    let worksheet = WORKSHEET_TEMPLATE.replace('{sheetDataPlaceholder}', sheetData);
    worksheet = worksheet.replace('{colPlaceholder}', colStyles);

    return worksheet;
  }

  /**
   * Prepares column configuration referenced in worksheet creation
   * @param {Array<ExcelColumn>} columnConfig column configuration
   */
  prepareColumnConfig(columnConfig) {
    if (!columnConfig) return;
    this.columns = columnConfig.map((config, idx) => {
      const xlsxCol = {
        ...config,
        refLetter: this.generateColumnLetter(idx),
        width: config.name.length + CELL_PADDING
      };

      return xlsxCol;
    });
  }

  /**
   * Recursively creates Excel column letter by column index
   * @param {number} colIndex zero-based column index
   * @returns {string} Excel Column letter
   */
  generateColumnLetter(colIndex) {
    if (typeof colIndex !== 'number') {
      return '';
    }

    const prefix = Math.floor(colIndex / 26);
    const letter = String.fromCharCode(97 + (colIndex % 26)).toUpperCase();
    if (prefix === 0) {
      return letter;
    }

    return this.generateColumnLetter(prefix - 1) + letter;
  }

  /**
   * Factory function to format cells of different types
   * @param {XLSXColumn} col column
   * @param {any} value field value
   * @param {number} cellIndex column number
   * @param {number} rowIndex row number
   * @returns {string} xlsx xml formatted cell
   */
  formatCell(col, value, cellIndex, rowIndex) {
    const cellConfig = col;
    let cellType = VALID_TYPES.includes(cellConfig?.type) ? cellConfig.type : 'string';
    const cellRefLetter = cellConfig.refLetter || this.generateColumnLetter(cellIndex);
    const cellRef = `${cellRefLetter}${rowIndex}`;

    // update column width
    const valueWidth = String(value).length + CELL_PADDING;
    cellConfig.width = Math.max(valueWidth, cellConfig.width);

    // check if type is string, but contains only numbers
    // eslint-disable-next-line no-shadow
    const canCastAsNumber = (value) => {
      if (typeof value === 'number' && !Number.isNaN(Number(value))) {
        return true;
      }

      if (value?.length && !Number.isNaN(Number(value))) {
        return true;
      }

      return false;
    };
    if (cellType === 'string' && canCastAsNumber(value)) {
      cellType = 'number';
    }

    // eslint-disable-next-line no-shadow
    const formatTypedCell = (value, cellRef, type) => {
      // eslint-disable-next-line no-shadow
      const formatTimeCell = (dateStr, cellRef) => {
        const d = new Date(dateStr);

        if (!Number.isNaN(d.getTime())) {
          const excelDate = createExcelDate(d);
          return `<c r="${cellRef}" s="3"><v>${excelDate}</v></c>`;
        }

        return formatStringCell('', cellRef);
      };

      // eslint-disable-next-line no-shadow
      const formatDateCell = (dateStr, cellRef) => {
        const d = new Date(dateStr);

        if (!Number.isNaN(d.getTime())) {
          const excelDate = createExcelDate(d);
          return `<c r="${cellRef}" s="2"><v>${excelDate}</v></c>`;
        }

        return formatStringCell('', cellRef);
      };

      if (type === 'date') {
        return formatDateCell(value, cellRef);
      }

      if (type === 'time') {
        return formatTimeCell(value, cellRef);
      }

      // return value as number
      value = Number.isNaN(value) ? '' : value;
      value ??= '';
      return `<c r="${cellRef}"><v>${value}</v></c>`;
    };
    return cellType === 'string' ?
      formatStringCell(value, cellRef) :
      formatTypedCell(value, cellRef, cellType);
  }

  /**
   * Create xlsx xml formatted row
   * @param {Record<string, any>} row row data
   * @param {number} rowIndex row index
   * @returns {string} xlsx xml formatted row
   */
  formatRow(row, rowIndex) {
    const rowCells = this.columns.reduce((rowString, col, idx) => {
      const value = row[col.id] || row[col.field];
      const cell = this.formatCell(col, value, idx, rowIndex);
      return `${rowString}${cell}`;
    }, '');

    return `<row r="${rowIndex}">${rowCells}</row>`;
  }

  /**
   * Creates xlsx formatted rows
   * @param {Array<Record<string, any>>} rows array of row data
   * @returns {string} xml formatted rows
   */
  generateRows(rows) {
    return rows.reduce((prevRow, row, idx) => {
      // Excel rows start at 1 (reserved for header row)
      const rowIndex = idx + 2;
      return prevRow + this.formatRow(row, rowIndex);
    }, '');
  }

  /**
   * Clean up any references
   */
  destroy() {
    this.columns = [];
  }
}
